import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PODetailView } from "../features/purchase/podetail/PODetailView";
import { BillWorkflow } from "../features/purchase/BillWorkflow";
import { OrderFollowUp } from "../features/purchase/OrderFollowUp";
import { RequisitionToOrder } from "../features/purchase/RequisitionToOrder";
import { docAction, getPurchaseDoc, receiptAction } from "../api/purchases";

const LIST_OF = { requisition: "/purchases/requisitions", purchase_order: "/purchases/orders",
                  receipt: "/purchases/receipts", bill: "/purchases/bills" };

const FOLLOW = ["sent", "partially_received", "received"];

// صفحة مستقلة نظيفة — بلا تبويبات ولا شريط علوي ولا معلومات مستخدم.
export default function PurchaseDocPage() {
  const { t } = useTranslation();
  const { docId } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () => getPurchaseDoc(docId).then(setDoc).catch(() => nav("/purchases")),
    [docId, nav],
  );

  useEffect(() => { load(); }, [load]);

  const run = async (action) => {
    setBusy(true);
    try {
      let reason = "";
      if (["reject", "cancel"].includes(action)) {
        reason = window.prompt(t(`purchases.confirmPrompts.${action}`,
                                 t("purchases.reasonPrompt"))) || "";
        if (!reason) return;
      } else if (action === "close") {
        if (!window.confirm(t("purchases.confirmPrompts.close"))) return;
      }
      const caller = doc.doc_type === "receipt" ? receiptAction : docAction;
      await caller(doc.id, action, { reason, note: reason });
      toast.success(t(`purchases.actionSuccess.${action}`,
                      t(`purchases.actions.${action}`)));
      await load();
      return true;
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast.error(code ? t(`errors.${code}`, { defaultValue: t("errors.generic") })
                       : t("errors.generic"));
      return false;
    } finally { setBusy(false); }
  };

  const extra = !doc ? null : (
    <>
      {doc.doc_type === "requisition" && doc.state === "approved" ? (
        <RequisitionToOrder requisition={doc} onDone={load} />
      ) : null}
      {doc.doc_type === "purchase_order" && FOLLOW.includes(doc.state) ? (
        <OrderFollowUp order={doc} onDone={load} />
      ) : null}
      {doc.doc_type === "bill" ? <BillWorkflow bill={doc} onDone={load} /> : null}
    </>
  );

  return (
    <div data-testid="po-detail-page" className="h-[100dvh] w-full overflow-hidden bg-background">
      {doc ? (
        <PODetailView doc={doc} backTo={LIST_OF[doc.doc_type] || "/purchases"}
                      onRun={run} busy={busy} onDone={load} extra={extra} />
      ) : null}
    </div>
  );
}
